import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Grid,
  useTheme,
  useMediaQuery,
  CircularProgress,
  Chip,
  Avatar,
  Card,
  CardContent,
  alpha,
} from "@mui/material";
import StoreIcon from "@mui/icons-material/Store";
import ShoppingBasketIcon from "@mui/icons-material/ShoppingBasket";
import StorageIcon from "@mui/icons-material/Storage";

const API_BASE_URL = "https://uktomars.co.uk/api";
const REFRESH_INTERVAL = 60000;

const Dashboard = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [analytics, setAnalytics] = useState({
    products_count: 0,
    brands_count: 0,
    storage_usage: 0,
  });

  const formatStorage = useCallback((bytes) => {
    if (!bytes || bytes === 0) return "0 Bytes";
    const sizes = ["Bytes", "KB", "MB", "GB", "TB"];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return `${(bytes / Math.pow(1024, i)).toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })} ${sizes[i]}`;
  }, []);

  const stats = useMemo(
    () => [
      {
        title: "Products",
        value: analytics.products_count,
        icon: <ShoppingBasketIcon />,
        gradient: "linear-gradient(135deg, #f39c12, #e67e22)",
      },
      {
        title: "Brands",
        value: analytics.brands_count,
        icon: <StoreIcon />,
        gradient: "linear-gradient(135deg, #0984e3, #74b9ff)",
      },
      {
        title: "Storage",
        value: formatStorage(analytics.storage_usage),
        icon: <StorageIcon />,
        gradient: "linear-gradient(135deg, #2c3e50, #3498db)",
      },
    ],
    [analytics, formatStorage],
  );

  useEffect(() => {
    const fetchAnalytics = async () => {
      try {
        setError("");
        const res = await fetch(`${API_BASE_URL}/admin/analytics`);
        if (!res.ok) {
          throw new Error(`HTTP error! Status: ${res.status}`);
        }
        const data = await res.json();
        setAnalytics({
          products_count: Number(data?.products_count) || 0,
          brands_count: Number(data?.brands_count) || 0,
          storage_usage: Number(data?.storage_usage) || 0,
        });
      } catch (error) {
        setError("We couldn't load the dashboard right now.");
        console.error("Error fetching analytics:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchAnalytics();
    const intervalId = setInterval(fetchAnalytics, REFRESH_INTERVAL);
    return () => clearInterval(intervalId);
  }, []);

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "60vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "60vh",
          p: 3,
        }}
      >
        <Typography variant="h6" sx={{ mb: 1, textAlign: "center" }}>
          {error}
        </Typography>
        <Chip
          label="Retry"
          onClick={() => window.location.reload()}
          color="primary"
          variant="outlined"
          sx={{ cursor: "pointer" }}
        />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        p: { xs: 2, sm: 3 },
        pb: { xs: 8, sm: 10 },
        minHeight: "100vh",
        backgroundColor: theme.palette.background.default,
      }}
    >
      <Box sx={{ mb: { xs: 3, sm: 4 } }}>
        <Typography
          variant="h4"
          sx={{
            fontWeight: "bold",
            fontSize: { xs: "1.75rem", sm: "2rem", md: "2.125rem" },
            color: theme.palette.text.primary,
          }}
        >
          Analytics
        </Typography>
      </Box>

      <Grid container spacing={{ xs: 2, sm: 3 }} sx={{ mb: 3 }}>
        {stats.map((item, index) => (
          <Grid item xs={12} sm={6} md={4} key={index}>
            <Card
              sx={{
                background: item.gradient,
                color: "white",
                borderRadius: 3,
                position: "relative",
                overflow: "hidden",
                transition: "transform 0.2s",
                "&:hover": {
                  transform: "translateY(-4px)",
                  boxShadow: theme.shadows[8],
                },
              }}
            >
              <CardContent>
                <Typography
                  variant="subtitle2"
                  sx={{
                    mb: 1,
                    opacity: 0.9,
                    fontSize: { xs: "0.75rem", sm: "0.875rem" },
                  }}
                >
                  {item.title}
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: "bold",
                    fontSize: { xs: "1.5rem", sm: "1.75rem", md: "2rem" },
                    maxWidth: "85%",
                  }}
                >
                  {typeof item.value === "number"
                    ? item.value.toLocaleString()
                    : item.value}
                </Typography>
                <Avatar
                  sx={{
                    position: "absolute",
                    top: 10,
                    right: 10,
                    bgcolor: alpha("#ffffff", 0.2),
                    width: isMobile ? 34 : 40,
                    height: isMobile ? 34 : 40,
                  }}
                >
                  {item.icon}
                </Avatar>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Card
        sx={{
          borderRadius: 4,
          background: "linear-gradient(135deg, #1f2a37 0%, #2c3e50 100%)",
          color: "white",
          boxShadow: "0 14px 30px rgba(31, 42, 55, 0.35)",
        }}
      ></Card>
    </Box>
  );
};

export default Dashboard;
